import React, { useMemo, useState } from 'react';
import { StyleSheet, Pressable, View, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { isDarkTheme } from '@/constants/Colors';
import { getToolPresentation } from '@/components/Tools/toolPresentation';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import ToolFlagBadges from '@/components/Tools/ToolFlagBadges';
import ToolFavoriteHeart from '@/components/Tools/ToolFavoriteHeart';
import ToolAvatar from '@/components/Tools/ToolAvatar';
import useResponsiveness from '@/hooks/useResponsiveness';
import { paletteForSlug } from '@/utils/agent_catalog';

/**
 * Mirrors the old `CATEGORY_COLORS[slug]` API but routes every lookup through
 * the shared `colorForSlug` hash so tool cards / lists / heroes match every
 * other catalog surface (agents, tasks, decks, models). `'default'` and
 * unknown slugs both resolve to the neutral fallback palette.
 */
export const CATEGORY_COLORS = new Proxy(
  {},
  {
    get: (_target, slug) => {
      if (typeof slug !== 'string') return undefined;
      if (slug === 'default') return paletteForSlug('');
      return paletteForSlug(slug);
    },
  },
);

// Sensible fallback icon per outputType when the tool itself doesn't provide one
export const OUTPUT_TYPE_ICON = {
  text:       'align-left',
  image:      'image',
  video:      'video',
  audio:      'volume',
  music:      'music',
  code:       'code',
  document:   'file-lines',
  '3d-model': 'cube',
};

// Human labels for the footer chip. Anything not listed falls back to the
// raw outputType with the first letter capitalised.
export const OUTPUT_TYPE_LABEL = {
  text:       'Text',
  image:      'Image',
  video:      'Video',
  audio:      'Audio',
  music:      'Music',
  code:       'Code',
  document:   'Document',
  '3d-model': '3D model',
};

export const outputTypeLabel = (outputType) => {
  if (!outputType) return null;
  if (OUTPUT_TYPE_LABEL[outputType]) return OUTPUT_TYPE_LABEL[outputType];
  const s = String(outputType);
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const isWeb = Platform.OS === 'web';

const createStyles = (colors, compact, palette, measuredCardWidth) => StyleSheet.create({
  card: {
    width: measuredCardWidth > 0 ? measuredCardWidth : '100%',
    minWidth: 0,
    minHeight: compact ? 112 : 196,
    flexDirection: compact ? 'row' : 'column',
    alignItems: compact ? 'center' : 'stretch',
    padding: compact ? 16 : 22,
    gap: compact ? 14 : 24,
    borderRadius: 20,
    backgroundColor: palette.surface,
    borderWidth: 1,
    borderColor: palette.border,
    ...(isWeb ? { transition: 'border-color 150ms ease, background-color 150ms ease' } : {}),
  },
  identity: { flex: 1, minWidth: 0, justifyContent: 'flex-end', gap: 6, paddingRight: compact ? 20 : 0 },
  name: { fontSize: compact ? 16 : 18, lineHeight: compact ? 22 : 24, letterSpacing: -0.4 },
  summaryRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  description: { flex: 1, lineHeight: 19 },
  actions: { position: 'absolute', top: compact ? 8 : 16, right: compact ? 8 : 16 },
  arrow: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  bone: { backgroundColor: palette.preview, borderRadius: 8 },
});

function ToolCard({ tool, onPress, style: styleProp, measuredCardWidth = null, compact }) {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const isCompact = compact ?? isXS;
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const palette = getCatalogPalette(theme);
  const styles = useMemo(() => createStyles(colors, isCompact, palette, measuredCardWidth), [colors, isCompact, palette, measuredCardWidth]);
  const presentation = getToolPresentation(tool, isDarkTheme(theme));
  const avatarSize = isCompact ? 48 : 60;

  return (
    <View style={[styles.card, hovered && { borderColor: palette.activeBorder }, focused && { borderColor: colors.text_color_link }, styleProp]}>
      <Pressable
        onPress={() => onPress ? onPress(tool) : tool?.slug && router.push(`/tools/${tool.slug}`)}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        accessibilityRole="button"
        accessibilityLabel={presentation.name}
        style={[StyleSheet.absoluteFillObject, { borderRadius: 20 }]}
      />
      <View style={{ pointerEvents: 'none' }}>
        <ToolAvatar tool={tool} size={avatarSize} iconSize={26} borderRadius={16}
          backgroundColor={presentation.background} color={presentation.foreground} />
      </View>
      <View style={[styles.identity, { pointerEvents: 'none' }]}>
        <Text weight="bold" style={styles.name} numberOfLines={2}>{presentation.name}</Text>
        <ToolFlagBadges tool={tool} omit={['featured', 'new']} />
        <View style={styles.summaryRow}>
          <Text size="s" color="secondary" style={styles.description} numberOfLines={2}>{presentation.summary}</Text>
          <View style={[styles.arrow, (hovered || focused) && { backgroundColor: palette.hover }]}>
            <Icon name="arrow-right" size={13} color={colors.text_color_secondary} />
          </View>
        </View>
      </View>
      <View style={styles.actions}><ToolFavoriteHeart tool={tool} /></View>
    </View>
  );
}

export function ToolCardPlaceholder({ measuredCardWidth = null, compact } = {}) {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isCompact = compact ?? isXS;
  const styles = createStyles(colors, isCompact, getCatalogPalette(theme), measuredCardWidth);
  const avatarSize = isCompact ? 48 : 60;
  return (
    <View style={styles.card}>
      <View style={[styles.bone, { width: avatarSize, height: avatarSize, borderRadius: 16 }]} />
      <View style={styles.identity}>
        <View style={[styles.bone, { height: 20, width: '70%' }]} />
        <View style={[styles.bone, { height: 14, width: '90%' }]} />
      </View>
    </View>
  );
}

export default React.memo(ToolCard);
